import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import { capitalise } from 'lib/string/stringUtils'
import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { accommodationIndividualRoomTileGridChildEnforcer } from './AccommodationIndividualRoomRoomTileGridBase'
import useSaleUnitFormatting from 'hooks/useSaleUnitFormatting'

export const AccommodationIndividualRoomTileHeaderContainer = styled(VerticalSpacer)`
  grid-area: header;
  ${accommodationIndividualRoomTileGridChildEnforcer}
`

type Props = {
  saleUnit: string
  heading: string
  packageNumber?: number
  room: OfferPageRoom
  onTitleClick?: () => void
  /** @default true */
  showOccupancyLine?: boolean
  /** Passed to {@link formatOccupantsShort} (e.g. property max child ages on SPP). */
  occupancyFormatOptions?: Parameters<typeof formatOccupantsShort>[1]
  /**
   * Render the occupancy beside the sale-unit label ("Room 1  2 adults") rather than under the
   * room name. Keeps the physical room, the requested occupancy, and the rate visually separate.
   * @default false
   */
  occupancyOnSaleUnitLine?: boolean
}

function AccommodationIndividualRoomTileHeader(props: Props) {
  const {
    saleUnit,
    packageNumber,
    heading,
    room,
    onTitleClick,
    showOccupancyLine = true,
    occupancyFormatOptions,
    occupancyOnSaleUnitLine = false,
  } = props

  const intl = useIntl()
  const { formatSaleUnitLabel } = useSaleUnitFormatting()
  const capitalizedSaleUnit = formatSaleUnitLabel(capitalise(saleUnit))

  const roomDetailsAriaLabel = intl.formatMessage({
    id: 'J3Sx6p8IY1mkhTCChlK1',
    defaultMessage: 'Room details',
    description: 'Aria label for the room details button/link in the multi-room selection tile header.',
  })

  const saleUnitLabel = packageNumber
    ? intl.formatMessage(
        {
          id: 'poZ0kR6DH6gTT0g1KeBC',
          defaultMessage: '{saleUnit} {number}',
          description: 'Label combining the sale unit type (e.g. "Room", "Package") with its number.',
        },
        { saleUnit: capitalizedSaleUnit, number: packageNumber },
      )
    : undefined

  const occupancyLine = showOccupancyLine ? (
    <BodyText variant="medium" colour="light-foreground-alt">
      {formatOccupantsShort(room, occupancyFormatOptions)}
    </BodyText>
  ) : undefined

  return <AccommodationIndividualRoomTileHeaderContainer gap={12}>
    {!!saleUnitLabel && !occupancyOnSaleUnitLine && <div>
      <StructureLabel variant="standard">
        {saleUnitLabel}
      </StructureLabel>
    </div>}
    {!!saleUnitLabel && occupancyOnSaleUnitLine && <Group direction="horizontal" gap={8} verticalAlign="center">
      <StructureLabel variant="standard">
        {saleUnitLabel}
      </StructureLabel>
      {occupancyLine}
    </Group>}
    <div>
      <Clickable onClick={onTitleClick} aria-label={roomDetailsAriaLabel}>
        <Heading variant="heading4">
          {heading} <LineInfoCircleIcon size="S" />
        </Heading>
      </Clickable>
      {!occupancyOnSaleUnitLine && occupancyLine}
    </div>
  </AccommodationIndividualRoomTileHeaderContainer>
}

export default AccommodationIndividualRoomTileHeader
